import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { categories, kaspreadsheetUrl } from "@/mocks/categories";

export default function Navbar() {
  const { t } = useTranslation();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  const isTop = !scrolled;
  const textClass = isTop ? "text-background-50" : "text-foreground-950";
  const textMutedClass = isTop ? "text-background-100/80" : "text-foreground-700";
  const hoverClass = isTop ? "hover:text-background-100" : "hover:text-foreground-950";
  const navBgClass = scrolled ? "bg-background-50/95 backdrop-blur-md" : "bg-transparent";

  return (
    <>
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${navBgClass}`}>
        <div className="w-full px-4 md:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <Link to="/" className={`flex items-center gap-3 cursor-pointer group ${isTop ? "drop-shadow-sm" : ""}`}>
              <div className="w-9 h-9 md:w-10 md:h-10 rounded-full bg-primary-500 flex items-center justify-center flex-shrink-0">
                <i className="ri-file-list-3-line text-background-50 text-lg"></i>
              </div>
              <span className={`font-heading text-lg md:text-xl font-semibold whitespace-nowrap transition-colors ${textClass}`}>
                USFansSpreadsheet
              </span>
            </Link>

            {/* Desktop Nav Links */}
            <div className="hidden md:flex items-center gap-8">
              <Link to="/" className={`${textMutedClass} ${hoverClass} text-sm font-medium transition-colors whitespace-nowrap cursor-pointer`}>
                {t("nav.home")}
              </Link>

              {/* Categories Dropdown */}
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className={`flex items-center gap-1.5 ${textMutedClass} ${hoverClass} text-sm font-medium transition-colors whitespace-nowrap cursor-pointer`}
                >
                  {t("nav.categories")}
                  <i className={`ri-arrow-down-s-line text-sm transition-transform ${dropdownOpen ? "rotate-180" : ""}`}></i>
                </button>

                {dropdownOpen && (
                  <div className="absolute top-full right-0 mt-2 w-72 bg-background-50 rounded-lg overflow-hidden z-50" style={{ boxShadow: "0 4px 24px rgba(0,0,0,0.08)" }}>
                    <div className="py-2 max-h-80 overflow-y-auto">
                      {categories.map((cat) => (
                        <a
                          key={cat.id}
                          href={cat.url}
                          target="_blank"
                          rel="noopener nofollow"
                          className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors cursor-pointer"
                          onClick={() => setDropdownOpen(false)}
                        >
                          <div className="w-7 h-7 flex items-center justify-center rounded-md bg-accent-100 flex-shrink-0">
                            <i className={`${cat.icon} text-accent-600 text-sm`}></i>
                          </div>
                          <span className="whitespace-nowrap">{t(cat.nameKey)}</span>
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <Link to="/artykul/przewodnik-usfansspreadsheet" className={`${textMutedClass} ${hoverClass} text-sm font-medium transition-colors whitespace-nowrap cursor-pointer`}>
                {t("nav.articles")}
              </Link>

              <Link to="/przewodnik-zakupowy" className={`${textMutedClass} ${hoverClass} text-sm font-medium transition-colors whitespace-nowrap cursor-pointer`}>
                {t("nav.shopping_guide")}
              </Link>
            </div>

            {/* Desktop CTA Button */}
            <div className="hidden md:flex items-center gap-3">
              <a
                href={kaspreadsheetUrl}
                target="_blank"
                rel="noopener nofollow"
                className={`px-4 py-2 text-background-50 text-sm font-medium rounded-full transition-colors whitespace-nowrap cursor-pointer ${isTop ? "bg-background-50/20 backdrop-blur-sm text-background-50 hover:bg-background-50/30" : "bg-primary-500 hover:bg-primary-600"}`}
              >
                {t("hero.cta_buy")}
              </a>
            </div>

            {/* Mobile: CTA + Hamburger */}
            <div className="md:hidden flex items-center gap-2">
              <a
                href={kaspreadsheetUrl}
                target="_blank"
                rel="noopener nofollow"
                className={`px-3 py-1.5 text-xs font-medium rounded-full whitespace-nowrap cursor-pointer ${isTop ? "bg-background-50/20 backdrop-blur-sm text-background-50" : "bg-primary-500 text-background-50"}`}
              >
                {t("hero.cta_buy")}
              </a>
              <button
                onClick={() => setMobileOpen(true)}
                className={`w-9 h-9 flex items-center justify-center rounded-lg cursor-pointer transition-colors ${isTop ? "text-background-50 hover:bg-background-50/10" : "text-foreground-950 hover:bg-background-100"}`}
                aria-label="Menu"
              >
                <i className="ri-menu-line text-xl"></i>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-[60] md:hidden">
          <div className="absolute inset-0 bg-foreground-950/50" onClick={() => setMobileOpen(false)}></div>
          <div className="absolute right-0 top-0 bottom-0 w-72 bg-background-50 overflow-y-auto animate-slide-in">
            <div className="flex items-center justify-between p-4 border-b border-background-200">
              <span className="font-heading text-base font-semibold text-foreground-950">Menu</span>
              <button
                onClick={() => setMobileOpen(false)}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-100 cursor-pointer"
              >
                <i className="ri-close-line text-xl text-foreground-700"></i>
              </button>
            </div>
            <div className="py-2">
              <Link
                to="/"
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-3 px-4 py-3 text-sm font-medium text-foreground-700 hover:bg-background-100 cursor-pointer"
              >
                <div className="w-6 h-6 flex items-center justify-center">
                  <i className="ri-home-line text-accent-500"></i>
                </div>
                {t("nav.home")}
              </Link>

              <div className="px-4 py-2 text-xs font-semibold text-foreground-400 uppercase tracking-wider">{t("nav.categories")}</div>
              {categories.map((cat) => (
                <a
                  key={cat.id}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-600 hover:bg-background-100 cursor-pointer"
                >
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className={`${cat.icon} text-accent-500 text-sm`}></i>
                  </div>
                  {t(cat.nameKey)}
                </a>
              ))}

              <div className="border-t border-background-200 my-2"></div>

              <Link
                to="/artykul/przewodnik-usfansspreadsheet"
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-3 px-4 py-3 text-sm font-medium text-foreground-700 hover:bg-background-100 cursor-pointer"
              >
                <div className="w-6 h-6 flex items-center justify-center">
                  <i className="ri-article-line text-accent-500"></i>
                </div>
                {t("nav.articles")}
              </Link>

              <Link
                to="/przewodnik-zakupowy"
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-3 px-4 py-3 text-sm font-medium text-foreground-700 hover:bg-background-100 cursor-pointer"
              >
                <div className="w-6 h-6 flex items-center justify-center">
                  <i className="ri-shopping-bag-3-line text-accent-500"></i>
                </div>
                {t("nav.shopping_guide")}
              </Link>

              <div className="border-t border-background-200 my-2"></div>

              <a
                href={kaspreadsheetUrl}
                target="_blank"
                rel="noopener nofollow"
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-3 px-4 py-3 text-sm font-semibold text-accent-600 hover:bg-accent-50 cursor-pointer"
              >
                <div className="w-6 h-6 flex items-center justify-center">
                  <i className="ri-shopping-cart-line text-accent-500"></i>
                </div>
                {t("hero.cta_buy")}
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  );
}